import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { endpoints } from "@/hooks/endpoints";
import { getToken } from "@/hooks/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { invokeUserRefetch } from "@/store/slices/storeSlice";
import { CreditCard, Wallet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const WalletPage = () => {
  const { userProfileData } = useAppSelector((state) => state.store);
  const [scratchCode, setScratchCode] = useState("");
  const [isCharging, setIsCharging] = useState(false);
  const dispatch = useAppDispatch();

  const handleChargeWallet = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!scratchCode.trim()) {
      toast.error("يرجى إدخال كود الشحن");
      return;
    }

    setIsCharging(true);

    try {
      const res = await fetch(endpoints.walletRecharge, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        body: JSON.stringify({
          code: scratchCode.trim(),
        }),
      });

      if (res.ok) {
        toast.success("تم شحن المحفظة بنجاح");
        setScratchCode("");
        // Refresh the page or dispatch an action to update Redux store
        dispatch(invokeUserRefetch());
      } else {
        const result = await res.json();
        toast.error(result.data || "فشل شحن المحفظة. يرجى التحقق من الكود والمحاولة مرة أخرى.");
      }
    } catch (error) {
      console.error("Error charging wallet:", error);
      toast.error("حدث خطأ أثناء شحن المحفظة. يرجى المحاولة لاحقاً.");
    } finally {
      setIsCharging(false);
    }
  };

  return (
    <div
      dir="rtl"
      // className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-4 md:p-8"
      className="min-h-screen bg-[#f7f6f4] p-4 md:p-8"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Header */}
        {/* <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
              <Wallet className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            المحفظة
          </h1>
        </div> */}

        {/* Wallet Balance Card */}
        <Card className="shadow-lg border-2 border-blue-100 pt-0">
          <CardHeader className="bg-gradient-to-r from-primary/90 to-primary text-white rounded-t-xl py-2">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              رصيد المحفظة
            </CardTitle>
            <CardDescription className="text-blue-50">
              المبلغ المتاح للاستخدام
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-5xl md:text-6xl font-bold text-gray-900 mb-2">
                {userProfileData?.wallet_credit?.toFixed(2) || "0.00"}
                <span className="text-2xl text-gray-600 mr-2">جنيه</span>
              </div>
              {/* <p className="text-gray-500 text-sm">
                آخر تحديث: {new Date().toLocaleDateString("ar-EG")}
              </p> */}
            </div>
          </CardContent>
        </Card>

        {/* Recharge Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              شحن المحفظة
            </CardTitle>
            <CardDescription>أدخل كود الشحن لاستكمال العملية</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="space-y-4" onSubmit={handleChargeWallet}>
              <div className="space-y-2">
                <label
                  htmlFor="scratch-code"
                  className="block text-sm font-medium text-gray-700"
                >
                  كود الشحن
                </label>
                <Input
                  id="scratch-code"
                  type="text"
                  placeholder="أدخل كود الشحن هنا..."
                  value={scratchCode}
                  onChange={(e) => setScratchCode(e.target.value)}
                  disabled={isCharging}
                  className="text-lg tracking-wider"
                />
              </div>

              <Button
                type="submit"
                disabled={isCharging || !scratchCode.trim()}
                className="w-full h-12 text-lg"
                size="lg"
              >
                {isCharging ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    جارٍ الشحن...
                  </>
                ) : (
                  "شحن المحفظة"
                )}
              </Button>
            </form>

            {/* Help Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">
                إرشادات شحن المحفظة:
              </h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>خربش الكارت و ادخل كود الشحن</li>
                <li>تأكد من إدخال الكود بالكامل ودون مسافات</li>
                <li>يمكن استخدام كل كود مرة واحدة فقط</li>
                <li>تواصل مع الدعم الفني إذا واجهت مشكلة</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
